import { call, put, select, takeLatest, all } from 'redux-saga/effects';
import {
    overlayLoaded,
    setError,
    setLoading,
    setSelectedTariffId,
    setTariffs,
} from 'Container/App/action';
import { AppActions, OverlayError } from './enum';
import { Cookies } from '../../enums/network';
import { AppState, SelectedTariff } from './interface';
import { deleteCookie, RequestParams } from '@vfde-sails/utils';
import { requestHtml } from '@vfde-sails/utils';
import { selectAppState, selectSelected } from './selector';
import { getCMSConfig, getFiberObject, getTariffIds } from '../../util/cms';
import { Overlay } from '@vfde-brix/ws10/overlay';
import { debugInfo } from '../../util/debug';
import { setOverlayVisiblity } from '../../components/overlay';
import { isApiError } from '../AvailabilityCheck/util/typeGuard';
import { trackError, trackPageView } from '../../util/tracking';
import { JarvisOffersRequest, JarvisProduct, getJarvisOffers } from '@vfde-sails/avc-core';
import { updateTariffPricesBeforeAvc } from 'Container/AvailabilityCheck/util/jarvisHelper';
import { updateFloatingMobileNotification } from '../../components/sticky-button';
import { TrackingAvcStep } from '../../enums/tracking';
import { updatePageUrl } from '../../util/url';
import { HomesPassedType } from '../../enums/product';

export function* getOffersSaga() {
    try {
        yield put(setLoading(true));

        const tariffIds = getTariffIds();
        const { tariffId }: SelectedTariff = yield select(selectSelected);

        const request: JarvisOffersRequest = {
            availabilityCheck: false,
            salesChannel: 'PRE_FIB',
            selected: tariffId,
            id: tariffIds,
            regioCharge: [],
        };

        const tariffs: JarvisProduct[] = yield call(getJarvisOffers, request);
        const tariffsUpdated: JarvisProduct[] = yield call(updateTariffPricesBeforeAvc, tariffs);

        yield put(setTariffs(tariffsUpdated, true));
    } catch (error) {
        yield put(setError(error as Error));

        if (isApiError(error)) {
            trackError(error);
        }
    } finally {
        yield put(setLoading(false));
    }
}

export function* initOverlay() {
    try {
        const overlay: Overlay | undefined = getFiberObject()?.overlay;
        const avcConfig = getCMSConfig().avc;

        if (!overlay) { throw new Error(OverlayError.OVERLAY_MISSING); }
        if (!avcConfig) { throw new Error(OverlayError.CONFIG_MISSING_AVC); }

        const requestOptions: RequestParams = {};



        const content: string[] = yield all([
            call(requestHtml, avcConfig.addressForm, requestOptions),
            call(requestHtml, avcConfig.resultLayer, requestOptions),
        ]);

        overlay.injectContent(...content);

        yield put(overlayLoaded());
    } catch (error) {
        debugInfo('Could not initialize overlay: ', error);
    }
}

/**
 * Hides every overlay content and only shows the required content type.
 * Then opens the overlay.
 */
export function* openOverlaySaga() {
    try {
        yield call(setOverlayVisiblity);
    } catch (error) {
        debugInfo('Could not open overlay: ', error);
    }
}

export function* deleteAddressSaga() {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

    if (isSafari) {
        updateFloatingMobileNotification(true);
    }
    try {
        deleteCookie(Cookies.ADDRESS);
        deleteCookie(Cookies.ZHP_LM_MODULE);
    } catch (error) {
        debugInfo('Something went wrong while deleting cookie: ', error);
    }
}

/**
 * Tracks page view
 */
export function* trackPageViewSaga(avcStep?: TrackingAvcStep,
    accessSupplier?: string, availabilityIndicator?: string, homePassed?: HomesPassedType) {
    const state: AppState = yield select(selectAppState);

    if (state?.error) {
        return;
    }

    yield call(
        trackPageView,
        state.selectedTariff.tariff!,
        state.avcResult,
        avcStep,
        accessSupplier,
        availabilityIndicator,
        homePassed
    );
}

/**
 * Updates page url after tariff selection
 */
export function* setSelectedTariffSaga({ id }: ReturnType<typeof setSelectedTariffId>) {
    yield call(updatePageUrl, id);
    yield call(trackPageViewSaga);
}

export function* initStoreSaga() {
    yield call(getOffersSaga);
    yield call(initOverlay);
}

export default function* saga() {
    yield takeLatest(AppActions.INIT_STORE, initStoreSaga);
    yield takeLatest(AppActions.SET_SELECTED_TARIFF_ID, setSelectedTariffSaga);
    yield takeLatest(AppActions.OPEN_OVERLAY, openOverlaySaga);
    yield takeLatest(AppActions.DELETE_ADDRESS, deleteAddressSaga);
}
